// Collections show more products (prompt 121, sections/main-collection.liquid + snippets/product-grid.liquid).
// - The next page's cards load on their own when the "TO BE CONTINUED" button is ~1.5 screens below the visible
//   area, appended to the same grid with the same markup (section rendering). The button stays as the fallback:
//   a tap loads the next page the same way; without JS it is a plain link to ?page=N.
// - Back / reload: the pages loaded here and the scroll position (the card at the top of the screen) come back.
//   Kept per collection URL in sessionStorage; a fresh visit starts at page 1.
// - Other scripts see the new cards through the 'ssc:grid-more' event or their own MutationObservers (Spikers,
//   flagship pills). Filters / Sort re-render the section: the new button is picked up again.
import { getIntersectionRoot, getScrollTop, scrollTo, scrollContainerMediaQuery } from '@theme/scroll-container';

const AHEAD = 1.5; // screens
const store = {
  get(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  },
  remove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {}
  },
};

/** Key for this collection view: path + query without page / hash. */
function viewKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.sort();
  return `ssc-more:${url.pathname}?${url.searchParams}`;
}

const button = () => /** @type {HTMLAnchorElement | null} */ (document.querySelector('a.ssc-load-more[data-ssc-more]'));
const grid = () => document.querySelector('results-list [ref="grid"]');
const cards = (root) => /** @type {HTMLElement[]} */ ([...root.querySelectorAll(':scope > .product-grid__item[data-product-id]')]);

/** @type {Map<string, Promise<string>>} */
const pending = new Map();

/** The section HTML for a page link; reuses the page 2 Horizon's results list already prefetched. */
function pageHTML(href, sectionId) {
  if (pending.has(href)) return pending.get(href);
  const url = new URL(href, window.location.href);
  const results = /** @type {any} */ (document.querySelector('results-list'));
  const cached = results && results.pages instanceof Map ? results.pages.get(Number(url.searchParams.get('page'))) : null;
  const here = new URL(window.location.href);
  here.searchParams.set('page', url.searchParams.get('page') || '');
  here.hash = '';
  let promise;
  if (cached && here.search === url.search) {
    promise = Promise.resolve(cached);
  } else {
    url.searchParams.set('section_id', sectionId);
    promise = fetch(url.toString(), { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });
  }
  pending.set(href, promise);
  promise.catch(() => pending.delete(href));
  return promise;
}

let busy = false;

/** Appends the next page to the grid. Resolves true when cards were added. */
async function loadNext() {
  const link = button();
  const list = grid();
  if (busy || !link || !list) return false;
  busy = true;
  const href = link.getAttribute('href') || '';
  const label = link.querySelector('small');
  const labelText = label ? label.textContent : '';
  link.setAttribute('aria-busy', 'true');
  if (label) label.textContent = 'Loading…';
  try {
    const html = await pageHTML(href, link.dataset.sectionId || '');
    // Filters / Sort replaced the grid while this page was loading: drop it.
    if (!link.isConnected || grid() !== list) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextGrid = doc.querySelector('[ref="grid"]');
    if (!nextGrid) throw new Error('no grid');
    const have = new Set(cards(list).map((li) => li.dataset.productId));
    const fresh = cards(nextGrid).filter((li) => !have.has(li.dataset.productId));
    list.append(...fresh);
    const nextLink = doc.querySelector('a.ssc-load-more[data-ssc-more]');
    if (nextLink) {
      const nextHref = new URL(nextLink.getAttribute('href') || '', window.location.href);
      nextHref.searchParams.delete('section_id');
      link.setAttribute('href', nextHref.pathname + nextHref.search);
      const nextLabel = nextLink.querySelector('small');
      if (label && nextLabel) label.textContent = nextLabel.textContent;
    } else {
      link.remove();
    }
    const state = store.get(viewKey()) || { hrefs: [] };
    if (!state.hrefs.includes(href)) state.hrefs.push(href);
    store.set(viewKey(), state);
    document.dispatchEvent(new CustomEvent('ssc:grid-more', { detail: { cards: fresh } }));
    return fresh.length > 0;
  } catch (error) {
    if (label) label.textContent = labelText;
    return false;
  } finally {
    busy = false;
    link.removeAttribute('aria-busy');
  }
}

/** True when the button sits within AHEAD screens below the visible area (and isn't hidden). */
function near() {
  const link = button();
  if (!link || !link.offsetParent) return false;
  const root = getIntersectionRoot();
  const box = root ? root.getBoundingClientRect() : { top: 0, height: window.innerHeight };
  return link.getBoundingClientRect().top < box.top + box.height * (1 + AHEAD);
}

/** Loads pages until the button is out of reach (e.g. an anime pill hides most new cards). */
async function fill() {
  let guard = 0;
  while (near() && guard < 20) {
    guard += 1;
    if (!(await loadNext())) break;
  }
}

let observer = null;
let watched = null;

function watch() {
  const link = button();
  if (link === watched && observer) return;
  if (observer) observer.disconnect();
  watched = link;
  if (!link) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) fill();
    },
    { root: getIntersectionRoot(), rootMargin: `0px 0px ${AHEAD * 100}% 0px` }
  );
  observer.observe(link);
}

// The tap fallback.
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a.ssc-load-more[data-ssc-more]') : null;
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  loadNext().then(() => fill());
});

// Remember where the shopper was: the first card on screen and its distance from the top of the visible area.
window.addEventListener('pagehide', () => {
  const list = grid();
  const state = store.get(viewKey());
  if (!list || !state || !state.hrefs.length) return;
  const root = getIntersectionRoot();
  const top = root ? root.getBoundingClientRect().top : 0;
  const first = cards(list).find((li) => li.offsetParent && li.getBoundingClientRect().bottom > top);
  state.y = getScrollTop();
  state.anchor = first ? first.id : '';
  state.offset = first ? first.getBoundingClientRect().top - top : 0;
  store.set(viewKey(), state);
});

async function restore() {
  const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType('navigation')[0]);
  const type = navigation ? navigation.type : 'navigate';
  const state = store.get(viewKey());
  if (type !== 'back_forward' && type !== 'reload') {
    store.remove(viewKey());
    return;
  }
  if (!state || !state.hrefs || !state.hrefs.length) return;
  const link = button();
  if (!link) return;
  // Fetch every remembered page at once, append them in order.
  state.hrefs.forEach((href) => pageHTML(href, link.dataset.sectionId || ''));
  for (let i = 0; i < state.hrefs.length; i++) {
    if (!(await loadNext())) break;
  }
  const anchor = state.anchor ? document.getElementById(state.anchor) : null;
  if (anchor && anchor.offsetParent) {
    const root = getIntersectionRoot();
    const top = root ? root.getBoundingClientRect().top : 0;
    scrollTo({ top: getScrollTop() + anchor.getBoundingClientRect().top - top - (state.offset || 0), behavior: 'instant' });
  } else if (Number.isFinite(state.y)) {
    scrollTo({ top: state.y, behavior: 'instant' });
  }
}

// bfcache: the page comes back as it was, nothing to do.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) watch();
});

// Filters / Sort re-render the section: watch the new button.
const host = document.querySelector('results-list');
if (host) {
  new MutationObserver(() => {
    if (button() !== watched) watch();
  }).observe(host, { childList: true, subtree: true });
}
scrollContainerMediaQuery.addEventListener('change', () => {
  watched = null;
  watch();
});

restore().finally(() => {
  watch();
  fill();
});
